import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Zap } from 'lucide-react';
import { MLB_MATCHUP_SABOTAGES } from '@/data/sabotageCatalog';
import { RARITY_CONFIG, type SabotageRarity } from '@/types/sabotageStats';
import { cn } from '@/lib/utils';

interface MLBBetaSabotageProps {
  leagueId: string | null;
}

const rarityOrder: SabotageRarity[] = ['common', 'rare', 'legendary'];

const rarityBgClasses: Record<SabotageRarity, string> = {
  common: 'bg-muted/30 border-rarity-common/30',
  rare: 'bg-rarity-rare/5 border-rarity-rare/40',
  legendary: 'bg-rarity-legendary/5 border-rarity-legendary/50',
};

const MLBBetaSabotage = ({ leagueId }: MLBBetaSabotageProps) => {
  // Group sabotages by rarity
  const groupedSabotages = useMemo(() => {
    const groups: Record<SabotageRarity, typeof MLB_MATCHUP_SABOTAGES> = {
      common: [],
      rare: [],
      legendary: [],
    };
    for (const sab of MLB_MATCHUP_SABOTAGES) {
      groups[sab.rarity].push(sab);
    }
    return groups;
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-display font-bold">
          <span className="text-gradient-sabotage">Sabotage</span> Engine
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Learn about available sabotages and how they work
        </p>
      </div>

      {/* Sabotage catalog grouped by rarity */}
      {rarityOrder.map((rarity) => {
        const items = groupedSabotages[rarity];
        if (items.length === 0) return null;
        const cfg = RARITY_CONFIG[rarity];

        return (
          <Card
            key={rarity}
            className={cn(
              'glass-card transition-all',
              cfg.borderClass,
              cfg.glowClass,
            )}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                {rarity === 'common' && '⚪'}
                {rarity === 'rare' && '🔵'}
                {rarity === 'legendary' && '🟣'}
                {cfg.label}
                <Badge
                  variant="outline"
                  className={cn('text-[10px] ml-1 border', cfg.badgeClass)}
                >
                  {cfg.weeklyLimit === 0 ? '∞ / week' : `${cfg.weeklyLimit} / week`}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {items.map((sab, i) => (
                <div
                  key={i}
                  className={cn(
                    'w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left',
                    rarityBgClasses[rarity],
                    sab.color === 'success' && 'border-green-500/30 bg-green-500/5',
                  )}
                >
                  <span className="text-2xl">{sab.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm flex items-center gap-1.5">
                      {sab.name}
                      {sab.color === 'success' ? (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 border-green-500/50 text-green-500 bg-green-500/10">BUFF</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 border-red-500/50 text-red-400 bg-red-500/10">ATTACK</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{sab.description}</div>
                  </div>
                  <Badge
                    variant="outline"
                    className={cn('shrink-0 text-[10px] border', cfg.badgeClass)}
                  >
                    {cfg.label}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })}

      {/* How it works */}
      <Card className="glass-card border-primary/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            How Sabotages Work
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>Sabotages are applied during matchup weeks through the spin wheel on the H2H page.</p>
          <p><strong className="text-red-400">Attack</strong> cards target your opponent. <strong className="text-green-500">Buff</strong> cards boost your own team. Some cards affect <strong className="text-foreground">both teams</strong>. Choose wisely!</p>
          <p>Rarity determines weekly limits and the odds of landing on each sabotage during a spin.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default MLBBetaSabotage;
